import type {Command, EditorView, KeyBinding} from '@codemirror/view'
import {ActionBar, type IconButtonProps} from '@primer/react'
import type {Nodes} from 'mdast'

import {useSelectionContext} from '../context/SelectionContext'

interface ToolbarButtonProps
  extends Omit<
    IconButtonProps,
    'onClick' | 'keybindingHint' | 'onMouseDown' | 'variant' | 'aria-labelledby' | 'aria-pressed'
  > {
  view: EditorView | null
  command: KeyBinding | Command
  matchingNodeFilter: (node: Nodes) => boolean
  ['aria-label']: string
}

function buildKeybindingHint(keybinding: KeyBinding) {
  return keybinding.key?.replaceAll('+', 'Plus')?.replaceAll('-', '+')
}

export const ToolbarButton = ({command, view, matchingNodeFilter, ...props}: ToolbarButtonProps) => {
  const commandIsKeybinding = typeof command === 'object'
  const commandFn = commandIsKeybinding ? command.run : command

  const selectionContext = useSelectionContext()
  const isPressed = [...selectionContext].some(matchingNodeFilter)

  return (
    <ActionBar.IconButton
      variant="invisible"
      // Prevent focus leaving input:
      onMouseDown={e => e.preventDefault()}
      keybindingHint={commandIsKeybinding ? buildKeybindingHint(command) : undefined}
      disabled={!view}
      onClick={() => view && commandFn?.(view)}
      aria-pressed={isPressed}
      {...props}
    />
  )
}

ToolbarButton.displayName = 'MarkdownEditor.ToolbarButton'
